import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatNumber } from '../../utils/calculations';

const Benchmarks = () => {
  const { results, inputs } = useCalculator();

  if (!results) {
    return <div className="text-center py-8 text-gray-600">Loading benchmarks...</div>;
  }

  // Industry benchmarks for hospitals
  const benchmarks = [
    {
      metric: 'EHS FTE per Employee',
      yourValue: inputs.employeeCount > 0 ? results.totalFTEs / inputs.employeeCount : 0,
      industryLow: 1 / 400,
      industryHigh: 1 / 300,
      format: (val) => `1:${Math.round(1 / val)}`,
      description: 'Ratio of EHS staff to total employees'
    },
    {
      metric: 'EHS FTE per Hospital Bed',
      yourValue: inputs.patientBeds > 0 ? results.totalFTEs / inputs.patientBeds : 0,
      industryLow: 1 / 300,
      industryHigh: 1 / 250,
      format: (val) => inputs.patientBeds > 0 ? `1:${Math.round(1 / val)}` : 'N/A',
      description: 'Ratio of EHS staff to patient beds'
    },
    {
      metric: 'Annual Hours per FTE',
      yourValue: results.productiveHoursFTE,
      industryLow: 1450,
      industryHigh: 1700,
      format: (val) => `${Math.round(val)} hrs`,
      description: 'Productive hours available per year'
    },
    {
      metric: 'Average Salary per EHS FTE',
      yourValue: inputs.avgSalary,
      industryLow: 75000,
      industryHigh: 95000,
      format: (val) => `$${formatNumber(val)}`,
      description: 'Average annual salary for EHS professionals'
    },
    {
      metric: 'Fire Safety Hours (Annual)',
      yourValue: results.fireSafetyHours || 0,
      industryLow: 520,
      industryHigh: 780,
      format: (val) => `${Math.round(val)} hrs`,
      description: 'Annual hours for fire/life safety compliance'
    },
    {
      metric: 'Admin Time Percentage',
      yourValue: inputs.adminPercent,
      industryLow: 12,
      industryHigh: 18,
      format: (val) => `${val}%`,
      description: 'Time spent on admin tasks and training'
    }
  ];

  const getBenchmarkStatus = (value, low, high) => {
    if (value >= low && value <= high) return 'good';
    if (value < low * 0.8 || value > high * 1.2) return 'alert';
    return 'warning';
  };

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 p-4 rounded-lg">
        <h3 className="font-semibold text-lg text-gray-900 mb-2">📊 Industry Benchmark Comparison</h3>
        <p className="text-sm text-gray-700">
          Compare your hospital EHS staffing model against industry standards for academic hospitals
          and healthcare facilities. Data sourced from ASSE, ASHE, and healthcare safety surveys.
        </p>
      </div>

      <div className="space-y-3">
        {benchmarks.map((benchmark, index) => {
          const status = getBenchmarkStatus(benchmark.yourValue, benchmark.industryLow, benchmark.industryHigh);
          
          return (
            <div key={index} className="bg-white border-2 border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900">{benchmark.metric}</h4>
                  <p className="text-xs text-gray-600 mt-1">{benchmark.description}</p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  status === 'good' ? 'bg-green-100 text-green-800' :
                  status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {status === 'good' && '✓ Within Range'}
                  {status === 'warning' && '⚠ Review'}
                  {status === 'alert' && '⚠ Outside Range'}
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mt-3">
                <div className="text-center p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-600 mb-1">Industry Low</div>
                  <div className="font-bold text-gray-700">{benchmark.format(benchmark.industryLow)}</div>
                </div>
                
                <div className={`text-center p-2 rounded ${
                  status === 'good' ? 'bg-green-50' :
                  status === 'warning' ? 'bg-yellow-50' :
                  'bg-red-50'
                }`}>
                  <div className="text-xs text-gray-600 mb-1">Your Value</div>
                  <div className={`font-bold text-lg ${
                    status === 'good' ? 'text-green-700' :
                    status === 'warning' ? 'text-yellow-700' :
                    'text-red-700'
                  }`}>
                    {benchmark.format(benchmark.yourValue)}
                  </div>
                </div>
                
                <div className="text-center p-2 bg-gray-50 rounded">
                  <div className="text-xs text-gray-600 mb-1">Industry High</div>
                  <div className="font-bold text-gray-700">{benchmark.format(benchmark.industryHigh)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-gray-50 p-4 rounded-lg mt-6">
        <h4 className="font-semibold text-gray-900 mb-2">💡 Interpretation Guidelines</h4>
        <ul className="text-sm text-gray-700 space-y-1">
          <li><span className="text-green-700 font-semibold">✓ Within Range:</span> Your value aligns with industry standards</li>
          <li><span className="text-yellow-600 font-semibold">⚠ Review:</span> Slightly outside typical range, review recommended</li>
          <li><span className="text-red-600 font-semibold">⚠ Outside Range:</span> Significant variance, may indicate over/understaffing</li>
        </ul>
        <p className="text-xs text-gray-600 mt-3">
          <strong>Note:</strong> Benchmarks are guidelines. Your specific facility characteristics, regulatory environment,
          and risk profile may justify values outside these ranges.
        </p>
      </div>
    </div>
  );
};

export default Benchmarks;
